import React, { useEffect, useState } from 'react';
import api from '../../api/axiosConfig';
import type { OptionResearch } from './LiveSummaryDecisionBriefCard';
import './shortCoveringEvidenceCard.css';
import './liveSummaryFocus.css';

type Evidence = {
  version: 'PI_SHORT_COVERING_EVIDENCE_V1';
  ticker?: string;
  status: string;
  currentBalanceDate?: string;
  observedNetShortBalanceReductionShares?: string;
  currentPublishedAt?: string;
  eventDate?: string;
  missing?: string;
};
type Props = { ticker: string; name?: string; optionResearch?: OptionResearch | null };

/** Private research evidence and explicit publication-delay state, not trade advice. */
export default function ShortCoveringEvidenceCard({ ticker }: Props): React.ReactElement | null {
  const [evidence, setEvidence] = useState<Evidence | null>(null);
  const [loadState, setLoadState] = useState<'LOADING' | 'ERROR' | 'READY'>('LOADING');

  useEffect(() => {
    const controller = new AbortController();
    setEvidence(null);
    setLoadState('LOADING');
    if (!/^\d{6}$/.test(ticker)) return () => controller.abort();
    api.get<Evidence>(`/market-evidence/short-covering/${encodeURIComponent(ticker)}`, { signal: controller.signal })
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        if (data?.version === 'PI_SHORT_COVERING_EVIDENCE_V1') setEvidence(data);
        setLoadState('READY');
      })
      .catch(() => { if (!controller.signal.aborted) setLoadState('ERROR'); });
    return () => controller.abort();
  }, [ticker]);

  const status = evidence?.status;
  const supported = status === 'BALANCE_REDUCTION_SUPPORTS_COVERING_HYPOTHESIS';
  const increased = status === 'NET_SHORT_BALANCE_INCREASED_ON_EVENT_DAY';
  const unchanged = status === 'NET_SHORT_BALANCE_UNCHANGED';

  // Never treat publication delay or an unavailable endpoint as zero short interest.
  const isObservation = supported || increased || unchanged;
  const message = isObservation
    ? supported ? '공매도 순보유잔고 감소 관측' : increased ? '공매도 순보유잔고 증가 관측' : '공매도 순보유잔고 변화 없음'
    : status === 'WAITING_FOR_EVENT_DAY_KRX_BALANCE' ? '당일 공매도 잔고 공식 공표 대기'
    : status === 'NO_EVENT_DAY_PRICE' ? '해당 거래일의 검증된 가격·잔고 연결 자료가 없습니다.'
    : status === 'NO_KRX_DATA' ? '공식 공매도 잔고 관측 자료가 없습니다.'
    : status === 'RESEARCH_DISABLED' ? '공매도 연구 자료 연결이 비활성화돼 있습니다.'
    : status === 'DATA_SOURCE_UNAVAILABLE' ? '공매도 자료 저장소를 조회할 수 없습니다.'
    : status === 'INSUFFICIENT_DATA' || status === 'PRICE_ONLY_OBSERVATION' ? '공매도 판단에 필요한 독립 근거가 부족합니다.'
    : '공식 공매도 잔고와 가격 관계를 확인 중입니다.';
  if (!/^\d{6}$/.test(ticker)) return null;
  if (loadState === 'LOADING') return null;
  return (
    <section className="pi-direction-flow-note" aria-label="공매도 잔고 관측 상태">
      <span>공매도 순보유잔고 · 연구용</span>
      <strong>{loadState === 'ERROR' ? '공매도 자료 연결 상태 미확인' : message}</strong>
      <small>{evidence?.currentBalanceDate ? `${evidence.currentBalanceDate} 잔고 기준` : '확정된 잔고 기준일 없음'}
        {' · '}공매도 잔고는 지연 공표되며 실제 매수 주체·원인·향후 방향은 알 수 없습니다.</small>
    </section>
  );

}
